
import { useEffect, useRef } from 'react';

class Star {
    x: number;
    y: number;
    z: number;
    size: number;
    color: string;
    baseX: number;
    baseY: number;
    screenX: number = 0;
    screenY: number = 0;
    currentScale: number = 0;

    constructor(width: number, height: number) {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.z = Math.random() * width; // Depth
        this.baseX = this.x;
        this.baseY = this.y;
        this.size = Math.random() * 1.5;

        // Realistic star colors (mostly white/blueish, some slight yellow/red)
        const colors = ['#ffffff', '#f0f8ff', '#e6e6fa', '#fffacd'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(mouseX: number, mouseY: number, width: number, height: number) {
        // Move star towards screen (Z-axis movement) to simulate flying through space
        this.z -= 0.5; // Speed of flight

        // Reset if it passes the screen
        if (this.z <= 0) {
            this.z = width;
            this.x = Math.random() * width;
            this.y = Math.random() * height;
            this.baseX = this.x;
            this.baseY = this.y;
        }

        // Parallax effect based on mouse position
        // Stars closer (smaller z) move faster
        const depth = (width - this.z) / width;
        const offsetX = mouseX * depth * 2;
        const offsetY = mouseY * depth * 2;

        // Apply parallax to position
        // We need to map 3D coordinates to 2D screen space
        // Simple perspective projection
        const fov = 500;
        const scale = fov / (fov + this.z);

        // Center the expansion point
        const cx = width / 2;
        const cy = height / 2;

        // Calculate final 2D position
        // (this.x - cx) centers the coordinate system
        // * scale applies perspective
        // + cx + offsetX moves it back to screen coordinates and applies parallax
        this.screenX = (this.x - cx) * scale + cx + offsetX;
        this.screenY = (this.y - cy) * scale + cy + offsetY;

        this.currentScale = scale;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.currentScale > 0) {
            const size = this.size * this.currentScale;
            // Fade in/out based on depth
            const opacity = Math.min(1, this.currentScale * 1.5);

            ctx.beginPath();
            ctx.fillStyle = this.color;
            ctx.globalAlpha = opacity;
            ctx.arc(this.screenX, this.screenY, size, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1.0;
        }
    }
}

export function CursorEffect() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsRef = useRef<Star[]>([]);
    const animationFrameIdRef = useRef<number>(0);
    const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });
    const dimensionsRef = useRef({ width: 0, height: 0, centerX: 0, centerY: 0 });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const initCanvas = () => {
            dimensionsRef.current.width = window.innerWidth;
            dimensionsRef.current.height = window.innerHeight;
            dimensionsRef.current.centerX = dimensionsRef.current.width / 2;
            dimensionsRef.current.centerY = dimensionsRef.current.height / 2;
            canvas.width = dimensionsRef.current.width;
            canvas.height = dimensionsRef.current.height;
        };

        const createStars = () => {
            starsRef.current = [];
            for (let i = 0; i < 800; i++) {
                starsRef.current.push(new Star(dimensionsRef.current.width, dimensionsRef.current.height));
            }
        };

        const animate = () => {
            if (!ctx) return;

            // Clear with slight fade for trail effect, but very subtle for stars
            ctx.fillStyle = 'rgba(10, 10, 15, 0.4)';
            ctx.fillRect(0, 0, dimensionsRef.current.width, dimensionsRef.current.height);

            // Smooth mouse movement interpolation
            mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * 0.05;
            mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * 0.05;

            starsRef.current.forEach(star => {
                star.update(mouseRef.current.x, mouseRef.current.y, dimensionsRef.current.width, dimensionsRef.current.height);
                star.draw(ctx);
            });

            animationFrameIdRef.current = requestAnimationFrame(animate);
        };

        const onResize = () => {
            initCanvas();
            createStars();
        };

        const onMouseMove = (e: MouseEvent) => {
            mouseRef.current.targetX = (e.clientX - dimensionsRef.current.centerX) * 0.1;
            mouseRef.current.targetY = (e.clientY - dimensionsRef.current.centerY) * 0.1;
        };

        initCanvas();
        createStars();
        animate();

        window.addEventListener('resize', onResize);
        window.addEventListener('mousemove', onMouseMove);

        return () => {
            cancelAnimationFrame(animationFrameIdRef.current);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 0,
                background: 'radial-gradient(ellipse at bottom, #1b2735 0%, #090a0f 100%)'
            }}
        />
    );
}
